import React, {ChangeEvent, useState} from 'react';
import moment from 'moment';
import clsx from 'clsx';

import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isAnnotationTagsEnabled} from 'common/report/util/feature-sampling';
import {formatAnnotationDate} from 'common/report/model-resources/annotations/utils';
import {Annotation} from 'common/report/model-resources/annotations/types';
import {AnnotationTag} from 'common/report/model-resources/annotation-tags/types';
import {getReportName} from 'common/report/util';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useAnnotations} from 'common/contexts/react/annotations-context';
import {useAnnotationTags} from 'common/contexts/react/annotation-tags-context';
import {ButtonReact} from 'common/components/button';
import {UserIconReact} from 'common/widgets/user-icon';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {IconSizeType} from 'common/constants';
import {TagReact} from 'common/components/tag';
import {TagSize, TagTheme} from 'common/components/tag/types';
import {AnnotationTagInputReact} from 'common/widgets/annotation-tag-input/annotation-tag-input';
import {useTranslation} from 'common/i18n/hooks';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {MarkdownReact} from 'common/components/markdown';

import {AnnotationLocation, track, TrackingEvent, TrackingEventProperty} from '../tracking';
import styles from './index.module.styl';

export enum AnnotationViewType {
  DEFAULT = `default`,
  PREVIEW = `preview`,
}

export type ViewAnnotationsListParams = {
  activeAnnotationGroup?: Annotation[];
  canWriteAnnotations?: boolean;
  type?: AnnotationViewType;
  timeUnit: TimeUnit;
  showHoverAnnotation?: boolean;
  className?: string;
  location: AnnotationLocation;
};

const AnnotationRow = ({
  isPreview,
  canWriteAnnotations,
  annotation,
  timeUnit,
  location,
  showHoverAnnotation,
}: {
  isPreview: boolean;
  canWriteAnnotations: boolean;
  annotation: Annotation;
  timeUnit: TimeUnit;
  location: AnnotationLocation;
  showHoverAnnotation?: boolean;
}) => {
  const currentProjectResource = useCurrentProject().get().value;
  const {t} = useTranslation();
  const annotationsResource = useAnnotations();
  const annotationTagsResource = useAnnotationTags();
  const [selectedTags, setSelectedTags] = useState<{[id: number]: AnnotationTag}>({});
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [tagsDraft, setTagsDraft] = useState<AnnotationTag[] | undefined>(undefined);
  const [descriptionDraft, setDescriptionDraft] = useState<string | undefined>(undefined);
  const formatDate = (formatAnnotation: Annotation) => {
    if (currentProjectResource && isAnnotationTagsEnabled(currentProjectResource)) {
      return formatAnnotationDate(formatAnnotation.date, {
        timezone: currentProjectResource.timezone,
        timeUnit,
      });
    }
    switch (timeUnit) {
      case TimeUnit.Day:
      case TimeUnit.Hour:
      case TimeUnit.Minute:
        return moment(formatAnnotation.highchartsDate).format(`ha`);
      default:
        return moment(formatAnnotation.highchartsDate).format(`ha, MMM Do`);
    }
  };

  const edit = (): void => {
    setSelectedTags((annotation.tags || []).reduce((acc, tag) => ({...acc, [tag.id]: tag}), {}));
    setDescriptionDraft(annotation.description);
    setTagsDraft(annotation.tags);
    setIsEditing(true);
  };

  const deleteAnnotation = (): void => {
    setIsDeleting(true);
  };

  const handleDescriptionChanged = (ev: ChangeEvent<HTMLTextAreaElement>): void => {
    setDescriptionDraft(ev.target.value);
  };

  const save = async () => {
    setIsSaving(true);
    await annotationsResource
      .updateAnnotation(annotation.id, {
        description: descriptionDraft || ``,
        tags: tagsDraft,
      })
      .catch(() => {
        addToast({message: t(`chart-actions.annotations.failed-update`), type: ToastType.ERROR});
      });
    track(TrackingEvent.Edit, {
      [TrackingEventProperty.AnnotationText]: annotation.description,
      [TrackingEventProperty.AnnotationTimestamp]: moment(annotation.date).format(`YYYY-MM-DD HH:mm:ss`),
      [TrackingEventProperty.ReportName]: getReportName(),
      [TrackingEventProperty.AnnotationTagCount]: tagsDraft?.length,
      [TrackingEventProperty.AnnotationTagNames]: tagsDraft?.map((tag) => tag.name),
      [TrackingEventProperty.Location]: location,
    });
    setIsSaving(false);
    setDescriptionDraft(undefined);
    setTagsDraft(undefined);
    setIsEditing(false);
  };

  const editCanceled = () => {
    setIsEditing(false);
  };

  const deleteConfirmed = async () => {
    setIsSaving(true);
    await annotationsResource.deleteAnnotation(annotation.id).catch(() => {
      addToast({message: t(`chart-actions.annotations.failed-delete`), type: ToastType.ERROR});
    });
    track(TrackingEvent.Delete, {
      [TrackingEventProperty.AnnotationText]: annotation.description,
      [TrackingEventProperty.AnnotationTimestamp]: moment(annotation.date).format(`YYYY-MM-DD HH:mm:ss`),
      [TrackingEventProperty.ReportName]: getReportName(),
      [TrackingEventProperty.AnnotationTagCount]: tagsDraft?.length,
      [TrackingEventProperty.AnnotationTagNames]: tagsDraft?.map((tag) => tag.name),
      [TrackingEventProperty.Location]: location,
    });
    setIsSaving(false);
    setIsDeleting(false);
  };

  const deleteCanceled = () => {
    setIsDeleting(false);
  };

  const deleteTag = (tag: TODO) => {
    const newState = {...selectedTags};
    delete newState[tag.id];
    setSelectedTags(newState);
    setTagsDraft(Object.values(newState));
  };

  const clearTags = () => {
    setSelectedTags({});
    setTagsDraft([]);
  };

  const handleTagSelected = (ev: AnnotationTag) => {
    const newState = {...selectedTags};
    const newTag = annotationTagsResource.getTag({id: ev.id});
    if (newTag) {
      newState[newTag.id] = newTag;
    }
    setSelectedTags(newState);
    setTagsDraft(Object.values(newState));
  };

  return (
    <div
      className={clsx(styles.annotationRow, {
        [styles.isPreview]: isPreview,
        [styles.isActive]: annotation.hovered && !isEditing && !isDeleting,
        [styles.editing]: isEditing,
      })}
      elref={`annotationRow`}
      onMouseEnter={() => (showHoverAnnotation ? annotationsResource.setHovered(annotation.id, true) : null)}
      onMouseLeave={() => (showHoverAnnotation ? annotationsResource.setHovered(annotation.id, false) : null)}
    >
      {!isEditing ? (
        <div className={styles.annotationIcon}>
          <UserIconReact
            name={annotation.user && `${annotation.user.first_name} ${annotation.user.last_name}`}
            id={annotation.user && annotation.user.id}
            disableTooltip
            size={IconSizeType.Small}
          />
        </div>
      ) : null}
      <div className={styles.annotationContent}>
        <div className={styles.annotationHeader}>
          <div className={styles.annotationMeta}>
            {annotation.user ? (
              <div className={styles.annotationUserLabel}>
                {`${annotation.user.first_name} ${annotation.user.last_name && annotation.user.last_name[0]}`}
              </div>
            ) : null}
            <div className={styles.annotationDateLabel}>{formatDate(annotation)}</div>
          </div>
          {canWriteAnnotations && !isEditing && !isDeleting ? (
            <div className={styles.annotationActions}>
              <ButtonReact
                className={styles.annotationEditButton}
                icon={`edit`}
                theme={ButtonTheme.Secondary}
                elref={`annotationEdit`}
                onClick={() => edit()}
              />
              <ButtonReact
                className={styles.annotationDeleteButton}
                icon={`trashcan`}
                theme={ButtonTheme.Tertiary}
                type={ButtonType.Destructive}
                elref={`annotationDelete`}
                onClick={() => deleteAnnotation()}
              />
            </div>
          ) : null}
        </div>
        {isEditing ? (
          <div className={styles.annotationEditMode}>
            <textarea
              onInput={handleDescriptionChanged}
              elref={`annotationTextArea`}
              maxLength={512}
              defaultValue={annotation.description}
            />
            {isAnnotationTagsEnabled(currentProjectResource) && (
              <>
                <AnnotationTagInputReact
                  selectedTags={selectedTags}
                  onTagDeleted={deleteTag}
                  onInputCleared={clearTags}
                  onTagSelected={handleTagSelected}
                />
              </>
            )}
            <div className={styles.annotationEditActions}>
              <ButtonReact theme={ButtonTheme.Tertiary} onClick={editCanceled}>
                {t(`common.cancel`)}
              </ButtonReact>
              <ButtonReact
                theme={ButtonTheme.Primary}
                disabled={!descriptionDraft && !tagsDraft}
                elref={`annotationSave`}
                onClick={save}
                icon={isSaving ? `spinner` : ``}
              >
                {t(`common.save`)}
              </ButtonReact>
            </div>
          </div>
        ) : (
          <div>
            <div className={clsx(styles.annotationDescription, isPreview ? styles.isPreview : ``)}>
              <MarkdownReact text={annotation.description} linkify />
            </div>
            <div className={styles.annotationTags}>
              {annotation.tags?.map((tag) => {
                return (
                  <TagReact
                    className='.mp-tag-input-tag'
                    elref={`mpTagInputTag`}
                    size={TagSize.Small}
                    tagName={tag.name}
                    theme={TagTheme.Light}
                    key={`tag-${tag.id}`}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
      {isDeleting ? (
        <div className={styles.confirmDeleteContainer}>
          <div className={styles.confirmDeleteButtons}>
            <ButtonReact theme={ButtonTheme.Secondary} onClick={deleteCanceled}>
              {t(`common.cancel`)}
            </ButtonReact>
            <ButtonReact
              theme={ButtonTheme.Primary}
              type={ButtonType.Destructive}
              elref={`annotationDeleteConfirm`}
              onClick={deleteConfirmed}
              icon={isSaving ? `spinner` : ``}
            >
              {t(`common.delete`)}
            </ButtonReact>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export function ViewAnnotationsListReact({
  type = AnnotationViewType.DEFAULT,
  activeAnnotationGroup,
  timeUnit,
  canWriteAnnotations,
  showHoverAnnotation,
  className,
  location,
}: ViewAnnotationsListParams) {
  const {t} = useTranslation();
  const isPreview = type === AnnotationViewType.PREVIEW;

  const getActiveAnnotationGroup = () => {
    let annotationGroup = activeAnnotationGroup || [];
    const numAnnotations = annotationGroup.length;

    // Preview only shows 2, then has a + X more... below
    annotationGroup = isPreview ? annotationGroup.slice(0, 2) : annotationGroup;

    return {annotationGroup, numAnnotations};
  };

  const {numAnnotations, annotationGroup} = getActiveAnnotationGroup();

  return (
    <div className={clsx(className, styles.container)}>
      <div className={styles.annotationsList}>
        {annotationGroup.map((annotation: Annotation) => {
          return (
            <AnnotationRow
              isPreview={isPreview}
              canWriteAnnotations={!!canWriteAnnotations}
              annotation={annotation}
              timeUnit={timeUnit}
              key={annotation.id}
              location={location}
              showHoverAnnotation={showHoverAnnotation}
            />
          );
        })}
      </div>
      {isPreview && numAnnotations > 2 ? (
        <div className={styles.footer}>
          {t(`chart-actions.annotations.more-annotations`, {number: numAnnotations - 2})}
        </div>
      ) : null}
    </div>
  );
}
